/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Auth0Client, User } from "@auth0/auth0-spa-js";
import { Auth0Context } from "./Auth0Context";
import { Auth0ContextInterface, AppState } from "./types";

const DEFAULT_REDIREFCT_CALLBACK = (state = {}) =>
  window.history.pushState(
    state,
    window.document.title,
    window.location.origin
  );

export const useAuth0 = (): Auth0ContextInterface => useContext(Auth0Context);

type Auth0ProviderProps = PropsWithChildren<{
  onRedirectCallback: (state: AppState) => void;
  client: Auth0Client;
}>;

export function Auth0Provider({
  children,
  onRedirectCallback = DEFAULT_REDIREFCT_CALLBACK,
  client,
}: Auth0ProviderProps) {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const authenticated = await client.isAuthenticated();

    if (authenticated) {
      const userData = await client.getUser();
      setUser(userData);
    }
  }, [client]);

  useEffect(() => {
    const initAuth0 = async () => {
      if (
        window.location.search.includes("code=") &&
        window.location.search.includes("state=")
      ) {
        const { appState } = await client.handleRedirectCallback();
        onRedirectCallback(appState);
      }

      await client.checkSession();
      await fetchUser();

      setIsLoading(false);
    };
    initAuth0();
  }, [fetchUser, onRedirectCallback, client]);

  const handleRedirectCallback = async () => {
    setIsLoading(true);
    await client.handleRedirectCallback();
    const userData = await client.getUser();
    setIsLoading(false);
    setUser(userData);
  };

  return (
    <Auth0Context.Provider
      value={{
        user,
        isLoading,
        handleRedirectCallback,
        checkSession: (...p) => client.checkSession(...p),
        getTokenSilently: (...p) => client.getTokenSilently(...p),
        getIdTokenClaims: (...p) => client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => client.loginWithRedirect(...p),
        logout: (...p) => client.logout(...p),
      }}
    >
      {isLoading ? null : children}
    </Auth0Context.Provider>
  );
}
